import { useCallback, useEffect, useMemo, useState } from 'react';
import { getMaxStep, getStepContent } from './content';
import { applyTagRanking } from './ranking';
import { loadMorningBootState, saveMorningBootState } from './storage';
import {
  addItem,
  addToBacklogFromInterrupt,
  completeSession,
  finalizeRankings,
  getOrCreateTodaySession,
  getTodaySession,
  importSuggestions,
  removeItem,
  setMode,
  toggleTag,
  updateAdvanced,
  updateItem,
  updateSession,
} from './state';
import { nextStep, prevStep } from './steps';
import type { MorningBootContextAdapter } from './adapters/types';
import type {
  BootItem,
  BootMode,
  BootSession,
  BootTag,
  MorningBootState,
} from './types';

export interface UseMorningBootOptions {
  storageKey: string;
  adapter?: MorningBootContextAdapter;
}

export interface UseMorningBootReturn {
  state: MorningBootState;
  session: BootSession | null;
  stepContent: ReturnType<typeof getStepContent>;
  maxStep: number;
  northStarLabel: string;
  northStarText?: string;
  suggestedRoles: string[];
  setBootMode: (mode: BootMode) => void;
  refreshSuggestions: () => void;
  addSessionItem: (text: string, partial?: Partial<BootItem>) => void;
  updateSessionItem: (itemId: string, patch: Partial<BootItem>) => void;
  removeSessionItem: (itemId: string) => void;
  toggleItemTag: (itemId: string, tag: BootTag) => void;
  rankItemsForTag: (tag: BootTag, orderedIds: string[]) => void;
  confirmTopThree: () => void;
  setTodayFocus: (itemId: string) => void;
  scheduleTimebox: (scheduledAt: string, provider?: string) => void;
  advanceStep: () => void;
  goBackStep: () => void;
  goToStep: (step: number) => void;
  toggleRoleScanned: (role: string) => void;
  setCommsReviewed: (value: boolean) => void;
  addResearchItem: (text: string) => void;
  toggleHighlight: (itemId: string) => void;
  handleInterrupt: (option: 'slot' | 'guesstimate' | 'backlog', text: string) => void;
  complete: () => void;
}

function persist(storageKey: string, state: MorningBootState) {
  saveMorningBootState(storageKey, state);
}

export function useMorningBoot(options: UseMorningBootOptions): UseMorningBootReturn {
  const { storageKey, adapter } = options;

  const [state, setState] = useState<MorningBootState>(() => {
    const loaded = loadMorningBootState(storageKey);
    return getOrCreateTodaySession(loaded);
  });

  useEffect(() => {
    persist(storageKey, state);
  }, [storageKey, state]);

  const session = getTodaySession(state) ?? null;

  const northStarLabel = adapter?.northStarLabel ?? 'North Star';
  const northStarText = adapter?.northStarText;
  const suggestedRoles = adapter?.suggestRoles() ?? [];

  const stepContent = session
    ? getStepContent(session.mode, session.step)
    : undefined;
  const maxStep = session ? getMaxStep(session.mode) : 5;

  const setBootMode = useCallback((mode: BootMode) => {
    setState((prev) => setMode(prev, mode));
  }, []);

  const refreshSuggestions = useCallback(() => {
    if (!adapter || !session) return;
    const suggestions = adapter.suggestItems();
    setState((prev) => {
      const current = getTodaySession(prev);
      if (!current) return prev;
      return importSuggestions(prev, current.id, suggestions);
    });
  }, [adapter, session]);

  const mutateSession = useCallback(
    (fn: (prev: MorningBootState, sessionId: string) => MorningBootState) => {
      setState((prev) => {
        const current = getTodaySession(prev);
        if (!current) return prev;
        return fn(prev, current.id);
      });
    },
    []
  );

  const addSessionItem = useCallback(
    (text: string, partial?: Partial<BootItem>) => {
      if (!text.trim()) return;
      mutateSession((prev, id) => addItem(prev, id, text, partial));
    },
    [mutateSession]
  );

  const updateSessionItem = useCallback(
    (itemId: string, patch: Partial<BootItem>) => {
      mutateSession((prev, id) => updateItem(prev, id, itemId, patch));
    },
    [mutateSession]
  );

  const removeSessionItem = useCallback(
    (itemId: string) => {
      mutateSession((prev, id) => removeItem(prev, id, itemId));
    },
    [mutateSession]
  );

  const toggleItemTag = useCallback(
    (itemId: string, tag: BootTag) => {
      mutateSession((prev, id) => toggleTag(prev, id, itemId, tag));
    },
    [mutateSession]
  );

  const rankItemsForTag = useCallback(
    (tag: BootTag, orderedIds: string[]) => {
      mutateSession((prev, id) => {
        const current = getTodaySession(prev);
        if (!current) return prev;
        const items = applyTagRanking(orderedIds, current.items, tag);
        return updateSession(prev, id, { items });
      });
    },
    [mutateSession]
  );

  const confirmTopThree = useCallback(() => {
    mutateSession((prev, id) => finalizeRankings(prev, id));
  }, [mutateSession]);

  const setTodayFocus = useCallback(
    (itemId: string) => {
      mutateSession((prev, id) => updateSession(prev, id, { todayFocus: itemId }));
    },
    [mutateSession]
  );

  const scheduleTimebox = useCallback(
    (scheduledAt: string, provider?: string) => {
      mutateSession((prev, id) =>
        updateSession(prev, id, { timebox: { scheduledAt, provider } })
      );
    },
    [mutateSession]
  );

  const advanceStep = useCallback(() => {
    mutateSession((prev, id) => {
      const current = getTodaySession(prev);
      if (!current) return prev;
      return updateSession(prev, id, { step: nextStep(current.mode, current.step) });
    });
  }, [mutateSession]);

  const goBackStep = useCallback(() => {
    mutateSession((prev, id) => {
      const current = getTodaySession(prev);
      if (!current) return prev;
      return updateSession(prev, id, { step: prevStep(current.step) });
    });
  }, [mutateSession]);

  const goToStep = useCallback(
    (step: number) => {
      mutateSession((prev, id) => updateSession(prev, id, { step }));
    },
    [mutateSession]
  );

  const toggleRoleScanned = useCallback(
    (role: string) => {
      mutateSession((prev, id) => {
        const current = getTodaySession(prev);
        const scanned = current?.advanced?.rolesScanned ?? [];
        const rolesScanned = scanned.includes(role)
          ? scanned.filter((r) => r !== role)
          : [...scanned, role];
        return updateAdvanced(prev, id, { rolesScanned });
      });
    },
    [mutateSession]
  );

  const setCommsReviewed = useCallback(
    (value: boolean) => {
      mutateSession((prev, id) => updateAdvanced(prev, id, { commsReviewed: value }));
    },
    [mutateSession]
  );

  const addResearchItem = useCallback(
    (text: string) => {
      if (!text.trim()) return;
      mutateSession((prev, id) => {
        const current = getTodaySession(prev);
        const researchItems = [...(current?.advanced?.researchItems ?? []), text.trim()];
        const withResearch = updateAdvanced(prev, id, { researchItems });
        return addItem(withResearch, id, text, {
          source: 'manual',
          metadata: { research: true },
        });
      });
    },
    [mutateSession]
  );

  const toggleHighlight = useCallback(
    (itemId: string) => {
      mutateSession((prev, id) => {
        const current = getTodaySession(prev);
        const highlighted = current?.advanced?.highlightedItemIds ?? [];
        const highlightedItemIds = highlighted.includes(itemId)
          ? highlighted.filter((h) => h !== itemId)
          : [...highlighted, itemId];
        const item = current?.items.find((i) => i.id === itemId);
        let next = updateAdvanced(prev, id, { highlightedItemIds });
        if (item) {
          next = updateItem(next, id, itemId, {
            metadata: {
              ...item.metadata,
              commitment: !highlighted.includes(itemId),
            },
          });
        }
        return next;
      });
    },
    [mutateSession]
  );

  const handleInterrupt = useCallback(
    (option: 'slot' | 'guesstimate' | 'backlog', text: string) => {
      if (!text.trim()) return;
      if (option === 'backlog') {
        mutateSession((prev, id) => addToBacklogFromInterrupt(prev, id, text));
      } else {
        addSessionItem(text);
      }
    },
    [mutateSession, addSessionItem]
  );

  const complete = useCallback(() => {
    mutateSession((prev, id) => completeSession(prev, id));
  }, [mutateSession]);

  return useMemo(
    () => ({
      state,
      session,
      stepContent,
      maxStep,
      northStarLabel,
      northStarText,
      suggestedRoles,
      setBootMode,
      refreshSuggestions,
      addSessionItem,
      updateSessionItem,
      removeSessionItem,
      toggleItemTag,
      rankItemsForTag,
      confirmTopThree,
      setTodayFocus,
      scheduleTimebox,
      advanceStep,
      goBackStep,
      goToStep,
      toggleRoleScanned,
      setCommsReviewed,
      addResearchItem,
      toggleHighlight,
      handleInterrupt,
      complete,
    }),
    [
      state,
      session,
      stepContent,
      maxStep,
      northStarLabel,
      northStarText,
      suggestedRoles,
      setBootMode,
      refreshSuggestions,
      addSessionItem,
      updateSessionItem,
      removeSessionItem,
      toggleItemTag,
      rankItemsForTag,
      confirmTopThree,
      setTodayFocus,
      scheduleTimebox,
      advanceStep,
      goBackStep,
      goToStep,
      toggleRoleScanned,
      setCommsReviewed,
      addResearchItem,
      toggleHighlight,
      handleInterrupt,
      complete,
    ]
  );
}
